import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { Add, Edit } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { tableStyle } from "../../components";
import {
  addNewCompany,
  listAllCompanies,
  resetAllCompanies,
  updateCompany,
} from "../../store/actions/companiesActions";

export const CompanyDialog = ({ open, onClose, onSubmit, data }) => {
  const isUpdate = Boolean(data);

  const [form, setForm] = useState({
    _id: "",
    name: "",
    email: "",
    mobile: "",
    address: "",
    gst_number: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        _id: data?._id || "",
        name: data?.name || "",
        email: data?.email || "",
        mobile: data?.mobile || "",
        address: data?.address || "",
        gst_number: data?.gst_number || "",
      });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isUpdate ? "Update Company" : "Add New Company"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Company Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />

          <TextField
            type="email"
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
            disabled={isUpdate}
          />

          <TextField
            label="Mobile"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            fullWidth
          />

          <TextField
            label="Address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            fullWidth
          />

          <TextField
            label="GST Number"
            value={form.gst_number}
            onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>
          {isUpdate ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompaniesList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const { isLoading, companiesList, showViewMore } = useSelector(
    ({ loading, companies }) => {
      const root = companies?.companies || companies;
      return {
        isLoading: loading,
        companiesList: root?.list || [],
        showViewMore: root?.showViewMore || false,
      };
    }
  );

  useEffect(() => {
    dispatch(resetAllCompanies());
    dispatch(listAllCompanies());
  }, [dispatch]);

  const fetchMoreData = () => dispatch(listAllCompanies());

  const CustomFooter = () => (
    <GridFooterContainer>
      <Stack width="100%" alignItems="center">
        {showViewMore ? (
          <Button fullWidth onClick={fetchMoreData}>
            <b>View More</b>
          </Button>
        ) : (
          <Typography variant="h6">All Data Listed</Typography>
        )}
      </Stack>
    </GridFooterContainer>
  );

  const handleAdd = () => {
    setDialogData(null);
    setDialogOpen(true);
  };

  const handleUpdate = (row) => {
    setDialogData(row);
    setDialogOpen(true);
  };

  const handleSubmit = (form) => {
    if (dialogData) {
      dispatch(updateCompany({ data: form }));
    } else {
      dispatch(addNewCompany(form));
    }
    setDialogOpen(false);
    setDialogData(null); 
  };

  const columns = [
    {
      field: "",
      headerName: "#",
      width: 50,
      renderCell: (params) => params.api.getRowIndex(params.row._id) + 1,
    },
    { field: "name", headerName: "Company Name", width: 200 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "mobile", headerName: "mobile", width: 150 },
    {
      field: "update",
      headerName: "Update",
      width: 140,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<Edit fontSize="small" />}
          onClick={() => handleUpdate(row)}
        >
          Update
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px" mb={15}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Header title="COMPANIES" subtitle="Welcome to Companies" />

        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
          }}
          onClick={handleAdd}
        >
          <Add sx={{ mr: "10px" }} />
          Add New Company
        </Button>
      </Stack>

      <Box height="68vh" sx={tableStyle(colors)}>
        <DataGrid
          rows={companiesList}
          columns={columns}
          getRowId={(row) => row._id}
          components={{ Footer: CustomFooter }}
        />
      </Box>

      <CompanyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        data={dialogData}
      />
    </Box>
  );
};

export default CompaniesList;